import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, FileArchive, FolderUp, Home, UserMinus, Users } from "lucide-react";
import { api, qs, startDownload, useApi, waitForJob, type Job } from "../lib/api";
import { baseName, bytes, parentOf, segments, when } from "../lib/format";
import { FileIcon } from "./icons";
import { Button, ErrorNote, Modal, Spinner } from "./ui";

// ---------- share ----------

interface ShareRow {
  email: string;
  name: string | null;
  pending: boolean;
  createdAt: string;
}

export function ShareDialog({ path, onClose, onChanged }: { path: string; onClose: () => void; onChanged: () => void }) {
  const shares = useApi<{ shares: ShareRow[] }>(`/shares?${qs({ path })}`);
  const people = useApi<{ people: { email: string; name: string }[] }>("/people");
  const [input, setInput] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isFolder = path.endsWith("/");

  const nameFor = useMemo(() => new Map((people.data?.people ?? []).map((p) => [p.email, p.name])), [people.data]);
  const addFromInput = () => {
    // Accept "Name <email>", a bare email, or several separated by commas/semicolons/spaces.
    const found = input.match(/[^\s<>,;]+@[^\s<>,;]+/g) ?? [];
    if (!found.length) return setError(input.trim() ? "Enter an @intellias.net email address" : null);
    const bad = found.find((e) => !/@intellias\.net$/i.test(e));
    if (bad) return setError(`${bad} isn't an @intellias.net address`);
    setPicked([...new Set([...picked, ...found.map((e) => e.toLowerCase())])]);
    setInput("");
    setError(null);
  };
  const share = async () => {
    setBusy(true);
    setError(null);
    try {
      await api("/shares", { body: { path, emails: picked } });
      setPicked([]);
      await shares.reload();
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (email: string) => {
    try {
      await api("/shares/remove", { body: { path, email } });
      await shares.reload();
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Modal title={`Share "${baseName(path)}"`} onClose={onClose} wide footer={<Button onClick={onClose}>Done</Button>}>
      <p className="text-sm text-muted mb-3">
        People you add can view and download {isFolder ? "everything in this folder, including files added later" : "this file"}. They can't change or delete anything.
      </p>
      <div className="flex gap-2">
        <input
          list="sharefile-people"
          value={input}
          placeholder="Name or email@intellias.net"
          aria-label="Person to share with"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === ",") && (e.preventDefault(), addFromInput())}
          onBlur={() => input.trim() && addFromInput()}
          className="flex-1 min-w-0 h-10 rounded-lg border border-line bg-surface px-3 text-sm"
        />
        <datalist id="sharefile-people">
          {(people.data?.people ?? []).map((p) => (
            <option key={p.email} value={`${p.name} <${p.email}>`} />
          ))}
        </datalist>
        <Button variant="primary" busy={busy} disabled={!picked.length} onClick={share}>
          Share
        </Button>
      </div>
      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {picked.map((e) => (
            <span key={e} className="inline-flex items-center gap-1 rounded-full bg-accent-chip text-accent-text text-xs pl-2.5 pr-1 py-1">
              {nameFor.get(e) ?? e}
              <button onClick={() => setPicked(picked.filter((x) => x !== e))} className="rounded-full px-1 hover:bg-surface" aria-label={`Remove ${e}`}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {error && (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
      <h3 className="text-sm font-semibold mt-5 mb-2">People with access</h3>
      {shares.loading && !shares.data ? (
        <Spinner />
      ) : !shares.data?.shares.length ? (
        <p className="text-sm text-muted pb-2">Only you.</p>
      ) : (
        <ul className="divide-y divide-line border border-line rounded-lg mb-2">
          {shares.data.shares.map((s) => (
            <li key={s.email} className="flex items-center gap-3 px-3 py-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{s.name ?? s.email}</div>
                <div className="text-xs text-muted truncate">
                  {s.email}
                  {s.pending && " · hasn't signed in yet"} · shared {when(s.createdAt)}
                </div>
              </div>
              <Button variant="ghost" onClick={() => remove(s.email)} aria-label={`Stop sharing with ${s.email}`}>
                <UserMinus className="size-4" /> Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

// ---------- move ----------

export function MoveDialog({ paths, onClose, onDone }: { paths: string[]; onClose: () => void; onDone: () => void }) {
  const [at, setAt] = useState(parentOf(paths[0]));
  const listing = useApi<{ folders: { path: string; name: string }[] }>(`/files?${qs({ path: at })}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const blocked = (folder: string) => paths.some((p) => p.endsWith("/") && folder.startsWith(p));
  const sameFolder = paths.every((p) => parentOf(p) === at);

  const move = async () => {
    setBusy(true);
    setError(null);
    try {
      for (const [i, from] of paths.entries()) {
        const to = at + baseName(from) + (from.endsWith("/") ? "/" : "");
        setProgress(paths.length > 1 ? `Moving ${i + 1} of ${paths.length}…` : "Moving…");
        const r = await api<{ jobId?: string }>("/move", { body: { from, to } });
        if (r.jobId) {
          const j = await waitForJob(r.jobId, (j) => j.progress && setProgress(`Moving ${baseName(from)}: ${j.progress.done} of ${j.progress.total} files…`));
          if (j.status === "failed") throw new Error(j.error ?? "The move failed");
        }
      }
      onDone();
      onClose();
    } catch (e) {
      setError((e as Error).message);
      onDone();
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Modal
      title={paths.length === 1 ? `Move "${baseName(paths[0])}"` : `Move ${paths.length} items`}
      onClose={onClose}
      footer={
        <>
          <span className="text-sm text-muted mr-auto self-center">{progress}</span>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={sameFolder || blocked(at)} onClick={move}>
            Move here
          </Button>
        </>
      }
    >
      <nav className="flex items-center flex-wrap gap-1 text-sm mb-2" aria-label="Folder">
        <button className="inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-surface-2" onClick={() => setAt("")}>
          <Home className="size-4" /> My files
        </button>
        {segments(at).map((s, i, all) => (
          <span key={i} className="inline-flex items-center gap-1">
            <ChevronRight className="size-3.5 text-muted" />
            <button className="px-1.5 py-1 rounded hover:bg-surface-2" onClick={() => setAt(all.slice(0, i + 1).join("/") + "/")}>
              {s}
            </button>
          </span>
        ))}
      </nav>
      <div className="border border-line rounded-lg min-h-48 max-h-72 overflow-y-auto">
        {at && (
          <button className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-surface-2 text-muted" onClick={() => setAt(parentOf(at))}>
            <FolderUp className="size-4" /> Up a level
          </button>
        )}
        {listing.loading && !listing.data ? (
          <Spinner />
        ) : (
          listing.data?.folders.map((f) => (
            <button key={f.path} disabled={blocked(f.path)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-surface-2 disabled:opacity-40 text-left" onClick={() => setAt(f.path)}>
              <FileIcon name={f.name} folder /> {f.name}
            </button>
          ))
        )}
        {listing.data && !listing.data.folders.length && <p className="text-sm text-muted px-3 py-2">No folders here.</p>}
      </div>
      {error && (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
    </Modal>
  );
}

// ---------- ZIP ----------

export function ZipDialog({ owner, paths, onClose }: { owner?: string; paths: string[]; onClose: () => void }) {
  const [job, setJob] = useState<Job | null>(null);
  const [info, setInfo] = useState<{ files: number; bytes: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // One job per dialog, even when StrictMode runs the effect twice.
  const started = useRef<Promise<{ jobId: string; files: number; bytes: number }> | null>(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        started.current ??= api<{ jobId: string; files: number; bytes: number }>("/zips", { body: { owner, paths } });
        const r = await started.current;
        if (!live) return;
        setInfo({ files: r.files, bytes: r.bytes });
        const j = await waitForJob(r.jobId, (j) => live && setJob(j));
        if (!live) return;
        if (j.status === "done" && j.url) startDownload(j.url);
        if (j.status === "failed") setError(j.error ?? "The ZIP couldn't be built");
      } catch (e) {
        if (live) setError((e as Error).message);
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const pct = job?.progress?.total ? Math.floor((job.progress.done / job.progress.total) * 100) : 0;
  return (
    <Modal title="Download as ZIP" onClose={onClose} footer={<Button onClick={onClose}>{job?.status === "done" ? "Done" : "Close"}</Button>}>
      {error ? (
        <ErrorNote>{error}</ErrorNote>
      ) : job?.status === "done" ? (
        <div className="flex items-start gap-3">
          <FileArchive className="size-8 text-accent-text shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{job.fileName}</div>
            <div className="text-xs text-muted">{bytes(job.size)} · your download should start. The link works for 24 hours.</div>
            <Button className="mt-3" variant="primary" onClick={() => job.url && startDownload(job.url)}>
              Download again
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-sm text-ink-2">{info ? `Zipping ${info.files.toLocaleString()} file${info.files === 1 ? "" : "s"} (${bytes(info.bytes)})…` : "Checking the selection…"}</p>
          <div className="mt-3 h-1.5 rounded-full bg-surface-2 overflow-hidden">
            <div className="h-full bg-accent transition-[width]" style={{ width: `${Math.max(pct, 3)}%` }} />
          </div>
          <p className="text-xs text-muted mt-2">You can close this; the ZIP keeps building, but you'll need to start again to download it.</p>
        </div>
      )}
    </Modal>
  );
}

// ---------- executable warning ----------

export function ExecutableWarning({ name, sharedBy, onConfirm, onClose }: { name: string; sharedBy: { name: string; email: string } | null; onConfirm: () => void; onClose: () => void }) {
  return (
    <Modal
      title="Download a program?"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => (onConfirm(), onClose())}>
            Download
          </Button>
        </>
      }
    >
      <div className="flex gap-3 text-sm text-ink-2">
        <Users className="size-5 text-warn shrink-0 mt-0.5" />
        <div className="space-y-2">
          <p>
            <strong className="text-ink">{name}</strong> is a program or script{sharedBy ? ` shared by ${sharedBy.name} (${sharedBy.email})` : ""}.
          </p>
          <p>Only open it if you expected it from them. Windows will check it before it runs.</p>
        </div>
      </div>
    </Modal>
  );
}
